import { FormApi, SubmissionErrors } from 'final-form';
import React from 'react';
import { Field, Form } from 'react-final-form';
import Select from 'react-select';
import {
  Button,
  Col,
  Form as FormComponent,
  FormGroup,
  Input,
  Label,
  Spinner,
} from 'reactstrap';
import RenderDate from './DatePicker';

export interface PatientFormValues {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  mrn?: string;
  gender?: { value: string; label: string };
}

const RenderNumber = ({
  name,
  label,
  placeholder,
  disabled,
}: {
  name: string;
  label: string;
  placeholder?: string;
  disabled?: boolean;
}) => (
  <FormGroup row>
    <Label for={name} sm={2}>
      {label}
    </Label>
    <Col sm={10}>
      <Field<string>
        name={name}
        disabled={disabled}
        placeholder={placeholder}
        validate={(value: string | undefined) =>
          !value || value.length === 0 ? 'This field is required' : undefined
        }>
        {({ input, meta, ...rest }) => (
          <div>
            <Input {...input} {...rest} type="number" invalid={!!meta.error} />
            <span
              className={
                meta.error ? 'text-danger visible' : 'text-danger invisible'
              }>
              {meta.error ? meta.error : 'All good'}
            </span>
          </div>
        )}
      </Field>
    </Col>
  </FormGroup>
);

const RenderText = ({
  name,
  label,
  placeholder,
  disabled,
}: {
  name: string;
  label: string;
  placeholder?: string;
  disabled?: boolean;
}) => (
  <FormGroup row>
    <Label for={name} sm={2}>
      {label}
    </Label>
    <Col sm={10}>
      <Field<string>
        name={name}
        disabled={disabled}
        placeholder={placeholder}
        validate={(value: string | undefined) =>
          !value || value.length === 0 ? 'This field is required' : undefined
        }>
        {({ input, meta, ...rest }) => (
          <div>
            <Input {...input} {...rest} type="text" invalid={!!meta.error} />
            <span
              className={
                meta.error ? 'text-danger visible' : 'text-danger invisible'
              }>
              {meta.error ? meta.error : 'All good'}
            </span>
          </div>
        )}
      </Field>
    </Col>
  </FormGroup>
);

const RenderSelect = ({
  name,
  label,
  placeholder,
  disabled,
}: {
  name: string;
  label: string;
  placeholder?: string;
  disabled?: boolean;
}) => (
  <FormGroup row>
    <Label for={name} sm={2}>
      {label}
    </Label>
    <Col sm={10}>
      <Field<{ value: string; label: string }>
        name={name}
        disabled={disabled}
        placeholder={placeholder}
        validate={(value: { value: string } | undefined) =>
          !value ? 'This field is required' : undefined
        }>
        {({ input, meta, ...rest }) => (
          <div>
            <Select
              {...input}
              {...rest}
              type="text"
              invalid={!!meta.error}
              options={[
                { value: 'male', label: 'Male' },
                { value: 'female', label: 'Female' },
                { value: 'other', label: 'Other' },
              ]}
              isClearable
            />
            {
              <span
                className={
                  meta.error ? 'text-danger visible' : 'text-danger invisible'
                }>
                {meta.error ? meta.error : 'All good'}
              </span>
            }
          </div>
        )}
      </Field>
    </Col>
  </FormGroup>
);

const PatientForm = (props: {
  initialValues?: PatientFormValues;
  onSubmit: (
    values: PatientFormValues,
    form: FormApi<PatientFormValues>,
    callback?: (errors?: SubmissionErrors) => void,
  ) =>
    | SubmissionErrors
    | Promise<SubmissionErrors | undefined>
    | undefined
    | void;
}) => {
  return (
    <Form onSubmit={props.onSubmit} initialValues={props.initialValues}>
      {({
        handleSubmit,
        form,
        submitting,
        pristine,
        hasValidationErrors,
        dirty,
      }) => (
        <FormComponent onSubmit={handleSubmit}>
          <RenderText
            name="firstName"
            placeholder="First Name"
            label="First Name"
          />
          <RenderText
            name="lastName"
            placeholder="Last Name"
            label="Last Name"
          />
          <RenderDate
            name="dateOfBirth"
            placeholder="Date of Birth"
            label="Date of Birth"
          />
          <RenderSelect
            name="gender"
            placeholder="Male / Female / Other"
            label="Gender"
          />
          {/* <RenderText name="organization" label="Organization" disabled /> */}
          <RenderNumber name="mrn" placeholder="MRN" label="MRN" />
          <Col className="d-flex justify-content-end">
            <Button color="success" disabled={hasValidationErrors || pristine}>
              {submitting ? <Spinner size="sm" /> : 'Submit'}
            </Button>
          </Col>
        </FormComponent>
      )}
    </Form>
  );
};

export default PatientForm;
